import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { ModalHeadersIndex } from "@modules/+new/shared/universal-modal/components/_headers/headers.index";
import { filter, map, take } from "rxjs/operators";
import { OpenModalDefinition } from "@modules/+new/shared/universal-modal/entities/modal-definition.interface";
import { ModalOptions } from "./entities/modal-options.interface";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { UNIVERSAL_MODAL_DEFAULT_CONFIG } from "@modules/+new/shared/universal-modal/universal-modal.defaults";
import { ModalClosePayload } from "@modules/+new/shared/universal-modal/entities/modal-close-payload.interface";
import { ModalComponent } from "./entities/modal-component.interface";
import { ExtractPayload } from "./entities/extract-payload.type";
import { Router } from "@angular/router";
import { IframeV4EventsHandlers } from "@modules/iframeV4/services/iframeV4-eventHandler.service";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { ModalFootersIndex } from "./components/_footers/footers.index";
import { PresetsIndex } from "@modules/+new/shared/universal-modal/components/_presets/presets.index";
import { ModalList } from "@modules/+new/shared/universal-modal/entities/modal-list.class";
import { ModalNodeDefinition } from "@modules/+new/shared/universal-modal/entities/modal-node.class";
import { PresetsComponentsEnum } from "@modules/+new/shared/universal-modal/entities/presets-components.enum";
import { PresetsTransforms } from "@modules/+new/shared/universal-modal/entities/presets-transforms.type";
import { PresetsPayloads } from "@modules/+new/shared/universal-modal/entities/presets-payloads.type";

/***
 * @description Service for handling modals. keep in mind it allows basic operations on current modal.
 * List is handled in ModalListClass, service should operate list but not directly modify it.
 */

@Injectable({
	providedIn: "root"
})
export class UniversalModalService {
	// modalList is default strategy for handing this view
	// if you want change it to some other form - consider writing new strategory and hook it here with common interface
	private modalList: ModalList = new ModalList();
	private rewindUrl: string;
	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public close$ = new BehaviorSubject<ModalClosePayload<any>>(null);
	public current$: BehaviorSubject<ModalNodeDefinition> = new BehaviorSubject<ModalNodeDefinition>(null);
	public isOpen$ = this.current$.pipe(map(Boolean));

	private showQueryParamsOnClose = true;

	constructor(
		private router: Router,
		private iframeEventHandlers: IframeV4EventsHandlers
	) {
		this.listenToModalList();
	}

	private listenToModalList = (): Subscription =>
		this.modalList.lastDefinition$.subscribe((def) => {
			this.showQueryParamsOnClose = def?.definition?.options?.showQueryParamsOnClose;
			if (this.rewindUrl) {
				this.router.navigateByUrl(this.rewindUrl);
				this.rewindUrl = null;
				return;
			}
			this.current$.next(def);
		});

	private prepConf = (def: Partial<ModalOptions>): ModalOptions => {
		const mergedConf = { ...UNIVERSAL_MODAL_DEFAULT_CONFIG, ...def };
		const headerComponent =
			mergedConf.wrap && mergedConf.header
				? ModalHeadersIndex[mergedConf.header || UNIVERSAL_MODAL_DEFAULT_CONFIG.header]
				: null;
		const footerComponent =
			mergedConf.wrap && mergedConf.footer
				? ModalFootersIndex[mergedConf.footer || UNIVERSAL_MODAL_DEFAULT_CONFIG.footer]
				: null;
		return { ...mergedConf, ...{ headerComponent, footerComponent } };
	};

	private flushPath = (): Promise<boolean> =>
		this.router.navigate(
			[{ outlets: { [ROUTER_OUTLET_NAME]: null } }],
			this.showQueryParamsOnClose ? { queryParamsHandling: "merge" } : undefined
		);

	public open<
		Def extends OpenModalDefinition,
		Comp extends Def["component"],
		// TODO: eslint fix
		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
		NewData extends Comp extends ModalComponent<any> ? ExtractPayload<Comp> : any
	>(definition: Def): Observable<ModalClosePayload<NewData>> {
		const options = this.prepConf(definition.options);
		const payload = { ...definition, ...{ options } };
		payload.options.clearPathOnOpen && this.flushPath();
		this.close$.next(null);
		this.modalList.add(payload);
		return this.close$.asObservable().pipe(
			filter((val) => !!val),
			take(1)
		);
	}

	public rewind = (stepsBack: number = 1): void => {
		const prev = this.modalList.prev;
		this.rewindUrl = prev?.definition?.url || null;
		this.modalList.revert(stepsBack);
	};

	public openPreset(
		preset: PresetsComponentsEnum,
		data?: PresetsPayloads,
		transforms?: PresetsTransforms
	): Observable<ModalClosePayload<PresetsPayloads>> {
		const def = {
			...PresetsIndex[preset],
			data: { ...PresetsIndex[preset].data, ...data },
			transforms
		};
		return this.open(def);
	}

	public close(
		reason: ModalCloseReason,
		data?: unknown,
		force: boolean = false,
		notifyIframe: boolean = true
	): Promise<void> {
		const currentModalOptions = this.current$.getValue()?.definition.options;

		// if there is no current component, aight imma head out
		if (!currentModalOptions) {
			return;
		}

		if (force) {
			const { clearPathOnClose } = currentModalOptions;
			clearPathOnClose && this.flushPath();
			this.modalList.flush();
			this.iframeEventHandlers.notifyModalClose();
			this.close$.next({ reason, data });
			return;
		}

		if (this.shouldShowPreviousModal(reason, currentModalOptions)) {
			this.rewind();
			return;
		}

		if (this.shouldShowConfirmModal(reason, currentModalOptions)) {
			this.openPreset(PresetsComponentsEnum.confirm);
			return;
		}

		const { clearPathOnClose } = currentModalOptions;
		clearPathOnClose && this.flushPath();
		this.modalList.flush();
		notifyIframe && this.iframeEventHandlers.notifyModalClose();
		this.close$.next({ reason, data });
	}

	private shouldShowPreviousModal(reason: ModalCloseReason, options: ModalOptions): boolean {
		return reason === ModalCloseReason.escape && options.goBackOnEscKey;
	}

	private shouldShowConfirmModal(reason: ModalCloseReason, options: ModalOptions): boolean {
		return (reason === ModalCloseReason.cancel || reason === ModalCloseReason.escape) && options.confirmCancel;
	}
}
